import React, { useEffect, useState } from 'react';
import { Building2 } from 'lucide-react';
import api from '@/lib/api';
import { currentMonthKey } from '@/lib/rentLedgerCache';

// Pre-carga (una sola vez por sesión) las consultas base de la app para que la navegación sea inmediata.
// La pantalla de progreso solo espera las consultas que necesita el Dashboard (+ catálogos ligeros);
// el resto (Cobranza, Finanzas, Contratos…) se precarga en segundo plano ya dentro de la app.
const WARMUP_MAX_MS = 25000; // tope de seguridad; normalmente entra al llegar a 100%
let warmedUp = false;

// Lo que el Dashboard pinta en su primera vista + catálogos ligeros
const criticalRequests = () => [
  ['/dashboard/stats'], ['/dashboard/available-units'], ['/properties'], ['/property-types'], ['/deudores'],
  ['/finance-categories'], ['/finance-categories/colors'], ['/deudores/settings'], ['/financial-accounts'],
];

// Precarga diferida: se lanza al entrar al Dashboard para no competir con sus consultas
const backgroundRequests = () => {
  const month = currentMonthKey();
  return [
    ['/termination-categories'], ['/maintenance-categories'], ['/external-services/types'], ['/external-services/types/colors'],
    ['/units'], ['/tenants'], ['/employees'], ['/external-services'], ['/maintenance'], ['/maintenance/blocked-availability'],
    ['/contracts'], ['/contracts/occupied-units/list'], ['/properties/occupancy'],
    ['/cobranza/anticipos'], ['/cobranza/rent-ledger', { month }], [`/income-expense?include_condonaciones=true&month=${month}`],
    ['/cobranza/monthly-status'],
  ];
};

export const AppWarmup = ({ children }) => {
  const [done, setDone] = useState(warmedUp);
  const [progress, setProgress] = useState({ n: 0, total: 1 });

  useEffect(() => {
    if (warmedUp) return undefined;
    const reqs = criticalRequests();
    let n = 0, finished = false;
    setProgress({ n: 0, total: reqs.length });
    const finish = () => {
      if (finished) return;
      finished = true; warmedUp = true; setDone(true);
      backgroundRequests().forEach(([url, params]) => api.get(url, { params, timeout: 30000, silent: true }).catch(() => {}));
    };
    const timer = setTimeout(finish, WARMUP_MAX_MS);
    reqs.forEach(([url, params]) => {
      api.get(url, { params, timeout: 30000, silent: true }).catch(() => {}).finally(() => {
        n += 1; setProgress({ n, total: reqs.length });
        if (n === reqs.length) { clearTimeout(timer); finish(); }
      });
    });
    // Precarga también el código de las páginas más usadas
    ['Dashboard', 'Finances', 'Payments', 'Deudores', 'RentMovements', 'Anticipos', 'Contracts', 'Properties', 'PropertyOccupancy', 'Tenants', 'Employees', 'ExternalServices', 'Maintenance', 'Settings']
      .forEach((n) => import(`../pages/${n}`).catch(() => {}));
    return () => clearTimeout(timer);
  }, []);

  if (done) return children;
  const pct = Math.round((progress.n / progress.total) * 100);
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6" data-testid="app-warmup">
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-900/40">
          <Building2 className="w-7 h-7 text-white" />
        </div>
        <h1 className="mt-5 text-xl font-bold text-white">Preparando tu información</h1>
        <p className="mt-1 text-sm text-slate-400">Cargando tu panel…</p>
        <div className="mt-6 h-2 w-full rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full bg-indigo-500 transition-[width] duration-300 ease-out" style={{ width: `${pct}%` }} data-testid="app-warmup-bar" />
        </div>
        <p className="mt-2 text-xs text-slate-500 tabular-nums" data-testid="app-warmup-pct">{pct}% · {progress.n}/{progress.total}</p>
      </div>
    </div>
  );
};

export default AppWarmup;
